import { ApolloCache } from '@apollo/client';
import { DataEntriesPagination, DataEntry } from 'general-ledger/fast-gl/types';

export function updateLedgersCachedData(
  cache: ApolloCache<unknown>,
  ledgerAccountId: number,
  dataEntries: DataEntry[],
  selectedDataEntries: DataEntry[] = [],
) {
  // Collect references to all the ledgers that were affected by the bulk edit
  const previousLedgerRefs = new Set<string | undefined>();
  const dataEntryIds = new Set(selectedDataEntries.map(dataEntry => dataEntry.id));
  selectedDataEntries.forEach(dataEntry => {
    const oldLedgerRef = cache.identify({
      id: dataEntry.ledgerAccount.id,
      __typename: 'LedgerAccount',
    });
    previousLedgerRefs.add(oldLedgerRef);
  });

  // For each ledger, remove the data entries that were moved from the bulk edit
  previousLedgerRefs.forEach(oldLedgerRef => {
    cache.modify({
      id: oldLedgerRef,
      fields: {
        dataEntries(
          existingDataEntries: Omit<DataEntriesPagination, 'dataEntries'> & {
            dataEntries: { __ref: string }[];
          },
        ) {
          const dataEntriesWithoutEdited = existingDataEntries.dataEntries.filter(
            // eslint-disable-next-line no-underscore-dangle
            existingEntry => !dataEntryIds.has(existingEntry.__ref.split(':')?.[1] || ''),
          );
          return {
            pageInfo: {
              ...existingDataEntries.pageInfo,
              total: existingDataEntries.pageInfo.total - dataEntryIds.size,
            },
            dataEntries: dataEntriesWithoutEdited,
          };
        },
      },
    });
  });

  const ledgerRef = cache.identify({ id: ledgerAccountId, __typename: 'LedgerAccount' });
  cache.modify({
    id: ledgerRef,
    fields: {
      dataEntries(existingDataEntries: DataEntriesPagination) {
        const newDataEntries = dataEntries.map(dataEntry => ({
          __ref: `DataEntry:${dataEntry.id}`,
        }));
        return {
          pageInfo: {
            ...existingDataEntries.pageInfo,
            total: existingDataEntries.pageInfo.total + newDataEntries.length,
          },
          dataEntries: [...newDataEntries, ...existingDataEntries.dataEntries],
        };
      },
    },
  });
}
